import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useActingAthlete } from './ActingAthleteContext';

/**
 * Server-resolved entitlements. PRESENTATION ONLY — every write path is
 * enforced independently by the backend, which 403s on its own. So the worst a
 * wrong value here can cause is a cosmetic bug, never an access-control hole.
 *
 * That asymmetry is why the failure mode is deliberately GENEROUS: if the fetch
 * fails we behave as if everything is granted rather than locking the UI. A
 * free athlete must never lose a free feature because a request timed out.
 */
const EntitlementsContext = createContext(null);

const OPEN = { tier: 'pro', features: null, limits: {} }; // features:null = "grant everything"

export function EntitlementsProvider({ children }) {
  const { isAuthenticated, API_URL } = useAuth();
  const { actingAthleteId } = useActingAthlete();
  const [state, setState] = useState(OPEN);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) { setState(OPEN); setLoading(false); return; }
    setLoading(true);
    try {
      // Pass as_athlete_id explicitly rather than relying on
      // ActingAthleteContext's axios interceptor: EntitlementsProvider is a
      // child of ActingAthleteProvider, and React fires passive effects
      // child-before-parent on both mount and update, so this fetch can go
      // out before that interceptor is installed/updated. An unscoped
      // request would resolve the GUARDIAN's own tier (they hold no
      // subscription) instead of the athlete's, silently gating the
      // athlete's paid features. The interceptor only sets the param when
      // absent, so this composes safely with it either way.
      //
      // Same reasoning for the Authorization header: EntitlementsProvider is
      // also a child of AuthProvider, and AuthProvider installs
      // axios.defaults.headers.common['Authorization'] inside its own mount
      // effect. Child-before-parent effect ordering means this fetch can
      // leave before that header exists, 401ing on every cold load. Do not
      // depend on ambient axios state — read the token directly, the same
      // way every other explicit-header call site in this codebase does.
      const { data } = await axios.get(`${API_URL}/entitlements`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: actingAthleteId ? { as_athlete_id: actingAthleteId } : undefined,
      });
      setState({
        tier: data?.tier === 'free' ? 'free' : 'pro',
        features: Array.isArray(data?.features) ? data.features : null,
        limits: (data && typeof data.limits === 'object' && data.limits) || {},
      });
    } catch {
      setState(OPEN);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, API_URL, actingAthleteId]);

  // actingAthleteId is a dependency: switching athletes changes the answer.
  useEffect(() => { refresh(); }, [refresh, actingAthleteId]);

  const has = useCallback(
    (key) => (state.features === null ? true : state.features.includes(key)),
    [state.features]
  );
  const limit = useCallback(
    (key) => (key in state.limits ? state.limits[key] : null),
    [state.limits]
  );

  const value = { tier: state.tier, features: state.features, limits: state.limits,
                  has, limit, loading, refresh };
  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

export const useEntitlements = () => {
  const ctx = useContext(EntitlementsContext);
  // Used outside the provider (tests, isolated renders): stay generous.
  if (!ctx) return { tier: 'pro', features: null, limits: {}, loading: false,
                     has: () => true, limit: () => null, refresh: () => {} };
  return ctx;
};
